import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Task } from '../../types';
import { toast } from 'sonner';
import { createTaskPayload } from '../../utils/taskCreation';

export interface QueuedTask extends Omit<Partial<Task>, 'status'> {
  id: string; // Temporary ID for queue management
  queued_at: string;
  status: 'queued' | 'creating' | 'created' | 'failed';
  error?: string;
}

interface TaskQueueContextType {
  queuedTasks: QueuedTask[];
  addTaskToQueue: (task: Partial<Task>) => boolean; // Return success/failure
  removeTaskFromQueue: (taskId: string) => void;
  clearQueue: () => void;
  clearCompletedTasks: () => void; // New function
  updateTaskStatus: (taskId: string, status: QueuedTask['status'], error?: string) => void;
  isQueueVisible: boolean;
  setQueueVisible: (visible: boolean) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
}

const TaskQueueContext = createContext<TaskQueueContextType | undefined>(undefined);

export const useTaskQueue = () => {
  const context = useContext(TaskQueueContext);
  if (!context) {
    throw new Error('useTaskQueue must be used within a TaskQueueProvider');
  }
  return context;
};

interface TaskQueueProviderProps {
  children: ReactNode;
}

export const TaskQueueProvider: React.FC<TaskQueueProviderProps> = ({ children }) => {
  const [queuedTasks, setQueuedTasks] = useState<QueuedTask[]>([]);
  const [isQueueVisible, setQueueVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const addTaskToQueue = (task: Partial<Task>): boolean => {
    const taskPayload = createTaskPayload({
      ...task,
      type: task.type || 'custom',
      status: 'open',
      priority: task.priority || 'medium',
    } as any);

    // Check for duplicates based on multiple criteria
    const isDuplicate = queuedTasks.some(existingTask => {
      // Skip tasks that are already created or failed
      if (existingTask.status === 'created' || existingTask.status === 'failed') {
        return false;
      }
      
      // Check for exact title match
      const titleMatch = existingTask.title === taskPayload.title;
      
      // Check for same page URL (strict match for performance tasks)
      const pageUrlMatch = (existingTask as any).page_url === (taskPayload as any).page_url;
      
      // For performance tasks, also check audit context if available
      const contextMatch = existingTask.context && taskPayload.context &&
        typeof existingTask.context === 'object' && typeof taskPayload.context === 'object' &&
        'audit_id' in existingTask.context && 'audit_id' in taskPayload.context
        ? (existingTask.context as any).audit_id === (taskPayload.context as any).audit_id
        : true;
      
      return titleMatch && pageUrlMatch && contextMatch;
    });
    if (isDuplicate) {
      console.log('Duplicate task detected, skipping:', taskPayload.title);
      toast.info(`Task "${taskPayload.title}" is already in queue`);
      return false; // Don't add duplicate
    }
    
    const queuedTask: QueuedTask = {
      ...taskPayload,
      id: `queue_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      queued_at: new Date().toISOString(),
      status: 'queued'
    };
    
    setQueuedTasks(prev => [...prev, queuedTask]);
    setQueueVisible(true); // Auto-show queue when task is added
    return true; // Successfully added
  };

  const removeTaskFromQueue = (taskId: string) => {
    setQueuedTasks(prev => prev.filter(task => task.id !== taskId));
  };
  const clearQueue = () => {
    setQueuedTasks([]);
    setQueueVisible(false);
  };

  const clearCompletedTasks = () => {
    setQueuedTasks(prev => prev.filter(task => 
      task.status !== 'created' && task.status !== 'failed'
    ));
  };

  const updateTaskStatus = (taskId: string, status: QueuedTask['status'], error?: string) => {
    setQueuedTasks(prev => prev.map(task => 
      task.id === taskId 
        ? { ...task, status, error }
        : task
    ));
  };

  return (    <TaskQueueContext.Provider value={{
      queuedTasks,
      addTaskToQueue,
      removeTaskFromQueue,
      clearQueue,
      clearCompletedTasks,
      updateTaskStatus,
      isQueueVisible,
      setQueueVisible,
      isProcessing,
      setIsProcessing
    }}>
      {children}
    </TaskQueueContext.Provider>
  );
};
